import {
  Badge,
  Box,
  Code,
  HStack,
  Icon,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react";
import React from "react";
import { FiCode, FiExternalLink, FiInfo } from "react-icons/fi";

interface PathAdvancedSettingsProps {
  metadata: string;
  metadataError: string | null;
  forceExternalBrowser: boolean;
  onMetadataChange: (value: string) => void;
  onForceExternalBrowserChange: (value: boolean) => void;
  disabled?: boolean;
}

export const PathAdvancedSettings: React.FC<PathAdvancedSettingsProps> = ({
  metadata,
  metadataError,
  forceExternalBrowser,
  onMetadataChange,
  onForceExternalBrowserChange,
  disabled = false
}) => {
  const handleMetadataChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onMetadataChange(e.target.value);
  };

  return (
    <Box
      bg="white"
      rounded="xl"
      shadow="sm"
      border="1px"
      borderColor="gray.200"
      overflow="visible"
      position="relative"
    >
      <Box
        px={6}
        py={4}
        bg="gradient.to-r"
        bgGradient="linear(to-r, blue.50, gray.50)"
        borderBottom="1px"
        borderColor="gray.200"
      >
        <HStack gap={2}>
          <Icon as={FiCode} boxSize={5} color="blue.600" />
          <Text fontWeight="semibold" fontSize="lg" color="gray.800">
            Advanced Settings
          </Text>
        </HStack>
      </Box>

      <Box p={6}>
        <VStack gap={6} align="stretch">
          {/* Page Metadata */}
          <Box>
            <HStack mb={2}>
              <Text fontSize="sm" fontWeight="medium">Page Metadata (JSON)</Text>
              <Badge colorScheme="gray" size="sm">
                Optional
              </Badge>
            </HStack>
            <Textarea
              value={metadata}
              onChange={handleMetadataChange}
              placeholder={`{
  "title": "Documentation Portal",
  "description": "Access our comprehensive API documentation",
  "customConfig": {
    "theme": "dark",
    "enableSearch": true
  }
}`}
              size="md"
              rows={8}
              fontFamily="mono"
              fontSize="sm"
              disabled={disabled}
              borderColor={metadataError ? "red.500" : undefined}
              _focus={{
                borderColor: metadataError ? "red.500" : "blue.500",
                boxShadow: `0 0 0 1px var(--chakra-colors-${
                  metadataError ? "red" : "blue"
                }-500)`
              }}
            />
            {metadataError && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {metadataError}
              </Text>
            )}
            <Text fontSize="xs" color="gray.500" mt={1}>
              This JSON will be injected as{" "}
              <Code fontSize="xs">window._metadata</Code> on the page. Use
              it to customize page behavior, titles, or pass configuration data.
            </Text>
          </Box>

          {/* Force External Browser */}
          <Box>
            <HStack mb={2}>
              <Icon as={FiExternalLink} color="gray.500" boxSize={4} />
              <Text fontSize="sm" fontWeight="medium">Force External Browser</Text>
              <Badge colorScheme="purple" size="sm">
                Recommended for Social Media
              </Badge>
            </HStack>
            <Box
              p={4}
              bg="gray.50"
              rounded="md"
              border="1px"
              borderColor="gray.200"
            >
              <HStack justify="space-between" align="center">
                <VStack align="start" gap={1} flex={1}>
                  <Text fontSize="sm" fontWeight="medium">
                    Open in Safari/Chrome
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Forces the link to open in the user's default browser instead
                    of embedded browsers (Facebook, Instagram, Twitter)
                  </Text>
                </VStack>
                <Box
                  position="relative"
                  display="inline-block"
                  width="50px"
                  height="28px"
                  bg={forceExternalBrowser ? "purple.500" : "gray.300"}
                  borderRadius="full"
                  transition="all 0.3s"
                  cursor={disabled ? "not-allowed" : "pointer"}
                  opacity={disabled ? 0.6 : 1}
                  onClick={() => {
                    if (!disabled) {
                      onForceExternalBrowserChange(!forceExternalBrowser);
                    }
                  }}
                  _hover={{
                    bg: disabled 
                      ? (forceExternalBrowser ? "purple.500" : "gray.300")
                      : (forceExternalBrowser ? "purple.600" : "gray.400")
                  }}
                  _after={{
                    content: '""',
                    position: "absolute",
                    width: "24px",
                    height: "24px",
                    borderRadius: "full",
                    bg: "white",
                    top: "2px",
                    left: forceExternalBrowser ? "24px" : "2px",
                    transition: "left 0.3s",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                  }}
                />
              </HStack>
              {forceExternalBrowser && (
                <Box
                  mt={3}
                  p={3}
                  bg="purple.50"
                  rounded="md"
                  borderLeft="3px solid"
                  borderColor="purple.400"
                >
                  <HStack gap={2} align="start">
                    <Icon as={FiInfo} color="purple.600" boxSize={4} mt={0.5} />
                    <VStack align="start" gap={1}>
                      <Text fontSize="xs" color="purple.800" fontWeight="medium">
                        How it works:
                      </Text>
                      <Text fontSize="xs" color="purple.700">
                        • Detects if the user is in an in-app browser
                      </Text>
                      <Text fontSize="xs" color="purple.700">
                        • Improves tracking accuracy and user experience
                      </Text>
                      <Text fontSize="xs" color="purple.700">
                        • Helps bypass in-app browser restrictions
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              )}
            </Box>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};